import type {
  ProductCardProps,
  AttributeColumn,
  DefaultColumn
} from '@hypercodestudio/basler-components';

import type { ProductListView } from '~/stores/productFilterStore/helper/resolveProductListView';

type ProductAttribute = ProductCardProps['product']['attributes'][number];

export type ProductListColumnLabels = {
  media: string;
  title: string;
  priceText: string;
  description: string;
};

export type PrepareProductListDisplayOptions = {
  displayMode: ProductListView;
  includePriceColumn?: boolean;
  discontinuedSpecs?: string[];
  columnLabels: ProductListColumnLabels;
};

export type PreparedProductListDisplay = {
  items: ProductCardProps[];
  defaultColumns: DefaultColumn[];
  attributeColumns: AttributeColumn[];
};

function buildDefaultColumns(
  items: ProductCardProps[],
  options: PrepareProductListDisplayOptions
): DefaultColumn[] {
  const columns: DefaultColumn[] = [
    {
      key: 'media',
      columnLabel: options.columnLabels.media,
      hideColumnLabel: true
    },
    {
      key: 'title',
      columnLabel: options.columnLabels.title
    }
  ];

  if (options.includePriceColumn) {
    columns.push({
      key: 'priceText',
      columnLabel: options.columnLabels.priceText
    });
  }

  const hasDescription = items.some((card: ProductCardProps) => {
    const description = card.product.description;
    return (
      typeof description === 'string' &&
      description !== ' ' &&
      description.trim() !== ''
    );
  });

  if (hasDescription) {
    columns.push({
      key: 'description',
      columnLabel: options.columnLabels.description,
      hideColumnLabel: true
    });
  }

  columns.push({
    key: 'ctaSlot',
    columnLabel: '',
    hideColumnLabel: true
  });

  return columns;
}

function buildTableListEntries(
  items: ProductCardProps[],
  options: PrepareProductListDisplayOptions
): PreparedProductListDisplay {
  const shouldAttributesMap: Record<string, ProductAttribute> = {};

  items.forEach((card: ProductCardProps) => {
    card.product.attributes.forEach((attribute: ProductAttribute) => {
      shouldAttributesMap[attribute.key] = attribute;
    });
  });

  const attributeColumns = Object.values(shouldAttributesMap).map(
    (attribute: ProductAttribute) => ({
      key: attribute.key,
      columnLabel: attribute.label
    })
  );

  if (options.discontinuedSpecs?.length) {
    attributeColumns.sort((left, right) => {
      return (
        options.discontinuedSpecs!.indexOf(left.key) -
        options.discontinuedSpecs!.indexOf(right.key)
      );
    });
  }

  const hasDescription = items.some((card: ProductCardProps) => {
    const description = card.product.description;
    return (
      typeof description === 'string' &&
      description !== ' ' &&
      description.trim() !== ''
    );
  });

  const list = items.map((card: ProductCardProps) => {
    const hasAttributeKeys = card.product.attributes.map(
      (attribute: ProductAttribute) => attribute.key
    );
    const orderedAttributes: ProductAttribute[] = [];

    attributeColumns.forEach((attributeColumn: AttributeColumn) => {
      if (!hasAttributeKeys.includes(attributeColumn.key)) {
        orderedAttributes.push({
          key: attributeColumn.key,
          label: shouldAttributesMap[attributeColumn.key].label,
          value: ' '
        });
        return;
      }

      const matchedAttribute = card.product.attributes.find(
        (attribute: ProductAttribute) => attribute.key === attributeColumn.key
      );
      if (matchedAttribute) {
        orderedAttributes.push(matchedAttribute);
      }
    });

    const suppressPrice = options.includePriceColumn === false;

    const nextCard: ProductCardProps = {
      ...card,
      product: {
        ...card.product,
        ...(suppressPrice && {
          priceText: '',
          pricePrefix: '',
          previousPriceText: undefined,
          pricePrefixAttentionText: undefined
        }),
        attributes: orderedAttributes,
        description: hasDescription
          ? card.product.description && card.product.description.trim() !== ''
            ? card.product.description
            : ' '
          : ''
      }
    };

    return nextCard;
  });

  return {
    items: list,
    defaultColumns: buildDefaultColumns(items, options),
    attributeColumns
  };
}

/**
 * Normalises ProductCard props for ProductList grid or table-list display,
 * mirroring the PLP Specifications list behaviour.
 */
export function prepareProductListDisplay(
  items: ProductCardProps[],
  options: PrepareProductListDisplayOptions
): PreparedProductListDisplay {
  if (options.displayMode === 'grid') {
    return {
      items,
      defaultColumns: buildDefaultColumns(items, options),
      attributeColumns: []
    };
  }

  return buildTableListEntries(items, options);
}
